import React, { useMemo, useCallback, useEffect } from 'react';
import styles from './GradeItem.module.scss';

import IconPencil from 'src/assets/icons/pencil.svg?react';
import IconArrowUp from 'src/assets/icons/dropdown.svg?react';
import FlagAU from 'src/assets/icons/icon_au.svg?react';
import FlagCA from 'src/assets/icons/icon_ca.svg?react';
import FlagUS from 'src/assets/icons/icon_us.svg?react';

import Button from '@src/components/button/Button';
import { Collapse } from '@src/utils/componentCasts';
import { useHistory } from 'react-router-dom';
import { useGetMultiCollectionLazyQuery } from '@src/api/graphql-codegen';
import Spinner from '@src/components/spinner/Spinner';
import CoverMultiContent from '@src/components/CoverMultiContent/CoverMultiContent';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useToast } from '@src/hooks';

type Props = {
  grade: any;
  gradeId: string;
  selectedId?: string | null;
  selected?: boolean;
  handleSeleted?: (selectId: string) => void;
  handleDelete?: (program: any) => void;
  handleEdit?: (program: any) => void;
  handleCustomize?: (program: any) => void;
  refresh?: () => void;
};

const getFlagIcon = (country: String) => {
  switch (country) {
    case "Australia": {
      return <div style={{width: 40}}><FlagAU width={40} /></div>;
    }
    case "Canada": {
      return <div style={{width: 40}}><FlagCA width={40}/></div>;
    }
    case "USA": {
      return <div style={{width: 40}}><FlagUS width={40}/></div>;
    }
    case "Canada & USA": {
      return (
        <>
          <div style={{width: 40}}><FlagCA width={40}/></div>
          <div style={{width: 40}}><FlagUS width={40}/></div>
        </>
      );
    }
    case "All Countries": {
      return <><FlagAU width={40}/><FlagCA width={40}/><FlagUS width={40}/></>;
    }
    default: {
      return null;
    }
  }
}

function GradeItem(props: Props) {
  const {
    grade,
    gradeId,
    selectedId,
    selected,
    handleSeleted,
    handleDelete,
    handleEdit,
    handleCustomize,
  } = props;
  const history = useHistory();
  const { successToast } = useToast();

  const [getMultiCollection, {data, loading}] = useGetMultiCollectionLazyQuery({
    variables: {input: {id: grade.id}},
    fetchPolicy: 'network-only'});

  const isSelected = useMemo(() => {
    return (selected && selectedId === gradeId) ? true : false;
  }, [selected, selectedId, gradeId]);

  useEffect(() => {
    if (isSelected) {
      getMultiCollection();
    }
  }, [getMultiCollection, isSelected])

  const hoverTitleViewStyle = useMemo(() => {
    const classes = [styles.hoverTitleView];
    
    return classes.join(' ');
  }, [])

  const handleSelectGrade = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSeleted?.(grade.id)
  }, [handleSeleted, grade.id])

  const handleCopyID = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    window.navigator.clipboard.writeText(gradeId);
    successToast("Multi-Collection ID Successfully copied successfully!");

  }, [gradeId, successToast])

  const handleDeleteGrade = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleDelete?.(grade)
  }, [handleDelete, grade])

  const handleEditGrade = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleEdit?.(grade)
  }, [handleEdit, grade])

  const handleCustomMultiCollection = useCallback(() => {
    handleCustomize?.(grade)
  }, [handleCustomize, grade])

  const onEditCollection = (id: string) => {
    history.push(`/super/collections/edit/${id}`);
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.backdrop} />
      <div className={styles.card}>
        <div className={styles.gradeTitleView}>
          <div className={styles.gradeGrayView} onClick={handleSelectGrade}>
            <div className={styles.titleSection}>
              <IconArrowUp className={[styles.arrowIcon, isSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={30} />
              {getFlagIcon(`${grade.country}`)}
              <div>
                <h1>{grade.name}</h1>
                {grade.description ? <span>{grade.description}</span> : null}
              </div>
            </div>
            <div className={styles.action}>
              <Button outline={true} size='small' label='Copy ID' className={styles.copyButton} onClick={handleCopyID}/>
              <Button outline={true} size='small' label='Delete' color='red' className={styles.deleteButton} onClick={handleDeleteGrade}/>
              <Button outline={true} size='small' label='Edit' className={styles.editButton} onClick={handleEditGrade}/>
              <div className={styles.viewCollection} rel="noreferrer" onClick={handleSelectGrade}>
                <span>{isSelected ? 'Hide Collections' : 'View Collections'}</span>
                <IconArrowUp className={[styles.arrowDownIcon, isSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={20} />
              </div>
            </div>
          </div>
        </div>
        <div>
        {loading ? <div className={styles.loadingWrapper}><Spinner /></div> :
          <Collapse isOpened={isSelected}>
            <div className={styles.content}>
              <div className={styles.topUnitView}>
                {data?.getMultiCollection && data?.getMultiCollection.content && data?.getMultiCollection.content.length > 0 ? 
                  (<div className={styles.topUnitSubView}>
                    <DndProvider backend={HTML5Backend}>
                      <div className={styles.lessonsView}>
                        <div className={styles.unitsDivWithFourGradient}>
                        {data?.getMultiCollection.content.map((collection: any, index: number) => {
                          return (
                            <CoverMultiContent key={index} content={collection} onEditCollection={onEditCollection}/>
                          )}
                        )}
                        </div>
                      </div>
                    </DndProvider>
                    {/* <div className={styles.gradientBackground}></div> */}
                  </div>
                  ) : (
                    <div className={styles.emptyCollectionView}>
                      <h2>No Collection Available</h2>
                      <span>Please create your first multi-collection</span>
                    </div>
                  )
                }
              </div>
              {<div>
                <div className={hoverTitleViewStyle}>
                  <div className={styles.hoverTitleSubView}>
                    <Button size='small' outline={true} className={styles.editCollectionsButton} label={"Edit Collections"}
                      leftIcon={<IconPencil className={styles.pencilIcon} />}
                      onClick={handleCustomMultiCollection}/>
                  </div>
                </div>
              </div>}
            </div>
          </Collapse>}
        </div>
      </div>
    </div>
  );
}

export default GradeItem;
